import { atom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { atomWithQuery } from 'jotai-tanstack-query'
import { MEMBER_STALE_TIME, callTrustedMember } from '~/lib/member/member'
import type { TrustedMemberResponse } from '~/lib/member/member'
import { env$ } from '~/stores/env'
import { isMember$, transition$ } from './stateMachine/state'
import type { ReservationInputEvent } from './stateMachine/statemachine'

type TrustedMemberQueryKey = readonly [string, string]
const trustedMemberQueryKey$ = atom((get) => {
  const env = get(env$)
  return ['trustedMember', env.MEMBER_ORIGIN] as TrustedMemberQueryKey
})
trustedMemberQueryKey$.debugLabel = 'trustedMemberQueryKey$'

export const trustedMemberQueryFn$ = atom({
  fn: async ({ queryKey: [, origin] }: { queryKey: TrustedMemberQueryKey }) =>
    callTrustedMember(origin),
})
trustedMemberQueryFn$.debugLabel = 'trustedMemberQueryFn$'

const trustedMemberQueryEnabled$ = atom((get) => {
  return trustedMemberQueryEnabled(get(isMember$))
})

const trustedMemberQueryEnabled =
  import.meta.env.VITE_MODE === 'ppg' ? ppgTrustedMemberQueryEnabled : ikyuTrustedMemberQueryEnabled

function ikyuTrustedMemberQueryEnabled(isMember: boolean) {
  return isMember
}

function ppgTrustedMemberQueryEnabled(_isMember: boolean) {
  // PPG の会員は常に信頼済として扱われる（state machine の RESERVE_INFO_LOADING を参照）ので、trustedMemberQuery$ を実行する必要がない
  // よって、常に false を返す
  return false
}

const trustedMemberQuery$ = atomWithQuery((get) => ({
  queryKey: get(trustedMemberQueryKey$),
  queryFn: get(trustedMemberQueryFn$).fn,
  staleTime: MEMBER_STALE_TIME,
  enabled: get(trustedMemberQueryEnabled$),
}))
trustedMemberQuery$.debugLabel = 'trustedMemberQuery$'

// ../specification.md 「atomEffect について」参照
export const listenTrustedMember$ = atomEffect((get, set) => {
  // RESERVE_INFO_LOADING へ戻る遷移を考慮し、isMember$ の変化を監視対象に加える
  if (!get(isMember$)) {
    return
  }

  const { status, data } = get(trustedMemberQuery$)
  if (status === 'pending') {
    return
  }
  // エラーの場合は、信頼済として扱う
  // @see: https://github.com/ikyucom/macaron-frontend/pull/2137#discussion_r1461002168
  if (status === 'error') {
    set(transition$, { type: 'trusted' })
    return
  }
  const event = nextEventOnLoad(data)
  if (event !== undefined) {
    set(transition$, event)
  }
})
listenTrustedMember$.debugLabel = 'listenTrustedMember$'

function nextEventOnLoad(
  data: TrustedMemberResponse
): Extract<ReservationInputEvent, { type: 'trusted' }> | undefined {
  return data.trusted ? { type: 'trusted' } : undefined
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('nextEventOnLoad(TrsutedMember)', () => {
    expect(nextEventOnLoad({ trusted: true })).toEqual({ type: 'trusted' })
    expect(nextEventOnLoad({ trusted: false })).toEqual(undefined)
  })
}
